import classNames from "classnames";
import Image from "next/image";
import { useEffect, useRef } from "react";
import errImg from "./assets/error-img.png";
import LoadingIcon from "./assets/loading-icon.svg";
import styles from "./index.module.css";

function SnowNoiseCanvas(props: { staticNoise?: boolean; snowSize?: number }) {
  const { staticNoise = false, snowSize = 1.5 } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // 等待下一帧确保容器已渲染
    const initCanvas = () => {
      const rect = container.getBoundingClientRect();
      const displayWidth = rect.width;
      const displayHeight = rect.height;

      // 如果容器尺寸为0，等待下一帧
      if (displayWidth === 0 || displayHeight === 0) {
        requestAnimationFrame(initCanvas);
        return;
      }

      // 设置 canvas 实际分辨率（考虑高 DPI 屏幕）
      const dpr = window.devicePixelRatio || 1;
      const canvasWidth = displayWidth * dpr;
      const canvasHeight = displayHeight * dpr;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const blockSize = snowSize * dpr;
      const frameInterval = 4; // 略微放慢节奏，动效更柔和
      const snowDensity = 0.28; // 降低数量以突出较大的雪花

      const drawSnowNoise = () => {
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        ctx.fillStyle = "rgb(204, 204, 204)";

        for (let y = 0; y < canvasHeight; y += blockSize) {
          for (let x = 0; x < canvasWidth; x += blockSize) {
            if (Math.random() > snowDensity) continue;

            ctx.globalAlpha = Math.random() * 0.35 + 0.25;
            ctx.fillRect(x, y, blockSize, blockSize);
          }
        }

        ctx.globalAlpha = 1;
      };

      // 雪花噪音动画
      let frameCount = 0;
      const animate = () => {
        if (frameCount % frameInterval === 0) {
          drawSnowNoise();
        }

        if (staticNoise) {
          return;
        }

        frameCount++;
        animationRef.current = requestAnimationFrame(animate);
      };

      animate();
    };

    initCanvas();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 w-full h-full">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full block"
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}

export function GeneratingVersionItem(props: {
  className?: string;
  staticNoise?: boolean;
  size: "small" | "large";
  isError?: boolean;
}) {
  const { className, staticNoise = false, isError = false, size } = props;

  const isSmall = size === "small";
  const snowSize = isSmall ? 0.5 : 1.5;

  if (isError) {
    return (
      <div className={classNames(styles.generatingItem, className)}>
        <SnowNoiseCanvas staticNoise snowSize={snowSize} />
        <div className="flex flex-col items-center justify-center gap-3">
          <Image src={errImg} width={isSmall ? "100" : "271"} alt="error" />
          {!isSmall && (
            <p className="text-center text-sm text-white font-normal">
              Server error — your content didn’t generate. Please try again
              later.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.generatingItem, className)}>
      <SnowNoiseCanvas staticNoise={staticNoise} snowSize={snowSize} />
      {isSmall && <LoadingIcon className="w-[50px] h-[50px] animate-spin" />}
      {!isSmall && (
        <div className="text-center text-sm text-white font-normal">
          <p>Generating takes about 40–60 seconds.</p>
          <p>We’ll keep you updated.</p>
        </div>
      )}
    </div>
  );
}
